import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useTheme } from '../../theme';

export interface TooltipProps {
  children: React.ReactNode;
  content: string;
  className?: string;
  delay?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  className = '',
  delay = 500,
  position = 'top'
}) => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [actualPosition, setActualPosition] = useState<'top' | 'bottom' | 'left' | 'right'>(
    position
  );
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveredRef = useRef(false);

  const calculateTooltipPosition = useCallback((): {
    position: 'top' | 'bottom' | 'left' | 'right';
    top: number;
    left: number;
  } => {
    if (!triggerRef.current) return { position: position, top: 0, left: 0 };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let finalPosition: 'top' | 'bottom' | 'left' | 'right' = position;
    let top = 0;
    let left = 0;

    // Calculate position based on trigger element and CSS transforms
    switch (position) {
      case 'top':
        // CSS: translateX(-50%) translateY(-100%) - so position at bottom-center of tooltip
        top = triggerRect.top - 8; // Gap from trigger
        left = triggerRect.left + triggerRect.width / 2; // Center horizontally
        if (triggerRect.top < 50) {
          finalPosition = 'bottom';
          top = triggerRect.bottom + 8; // Gap from trigger
          left = triggerRect.left + triggerRect.width / 2; // Center horizontally
        }
        break;
      case 'bottom':
        // CSS: translateX(-50%) - so position at top-center of tooltip
        top = triggerRect.bottom + 8; // Gap from trigger
        left = triggerRect.left + triggerRect.width / 2; // Center horizontally
        if (triggerRect.bottom > viewportHeight - 50) {
          finalPosition = 'top';
          top = triggerRect.top - 8; // Gap from trigger
          left = triggerRect.left + triggerRect.width / 2; // Center horizontally
        }
        break;
      case 'left':
        // CSS: translateX(-100%) translateY(-50%) - so position at right-center of tooltip
        top = triggerRect.top + triggerRect.height / 2; // Center vertically
        left = triggerRect.left - 8; // Gap from trigger
        if (triggerRect.left < 150) {
          finalPosition = 'right';
          top = triggerRect.top + triggerRect.height / 2; // Center vertically
          left = triggerRect.right + 8; // Gap from trigger
        }
        break;
      case 'right':
        // CSS: translateY(-50%) - so position at left-center of tooltip
        top = triggerRect.top + triggerRect.height / 2; // Center vertically
        left = triggerRect.right + 8; // Gap from trigger
        if (triggerRect.right > viewportWidth - 150) {
          finalPosition = 'left';
          top = triggerRect.top + triggerRect.height / 2; // Center vertically
          left = triggerRect.left - 8; // Gap from trigger
        }
        break;
    }

    return { position: finalPosition, top, left };
  }, [position]);

  useEffect(() => {
    // Update position when position prop changes (even if not visible)
    if (triggerRef.current) {
      const { position: finalPosition, top, left } = calculateTooltipPosition();
      setActualPosition(finalPosition);
      setTooltipPosition({ top, left });
      setIsReady(true);
    }
  }, [position, calculateTooltipPosition]);

  const handleMouseEnter = () => {
    isHoveredRef.current = true;

    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Show tooltip after delay
    timeoutRef.current = setTimeout(() => {
      // Only show if still hovered
      if (isHoveredRef.current && triggerRef.current) {
        // Calculate position before showing to prevent flicker
        const { position: finalPosition, top, left } = calculateTooltipPosition();
        setActualPosition(finalPosition);
        setTooltipPosition({ top, left });
        setIsReady(true);

        // Show tooltip after position is set
        requestAnimationFrame(() => {
          if (isHoveredRef.current) {
            setIsVisible(true);
          }
        });
      }
    }, delay);
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;

    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Hide tooltip with a small delay to prevent flickering
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setIsReady(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return (
    <TooltipContainer className={className}>
      <TriggerWrapper
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        {children}
      </TriggerWrapper>

      {isVisible &&
        isReady &&
        createPortal(
          <TooltipContent
            ref={tooltipRef}
            position={actualPosition}
            $theme={theme}
            data-position={actualPosition}
            style={{
              position: 'fixed',
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              zIndex: 10000,
              pointerEvents: 'none'
            }}
          >
            {content}
            <TooltipArrow position={actualPosition} $theme={theme} />
          </TooltipContent>,
          document.body
        )}
    </TooltipContainer>
  );
};

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerWrapper = styled.div`
  display: inline-block;
`;

const TooltipContent = styled.div<{
  position: 'top' | 'bottom' | 'left' | 'right';
  $theme: any;
}>`
  background: ${(props) => props.$theme.colors.messageBoxBg};
  color: ${(props) => props.$theme.colors.mainText};
  border: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};
  border-radius: ${(props) => props.$theme.tokens.radiusMd};
  padding: 6px 8px;
  font-size: 14px;
  font-weight: 500;
  white-space: normal;
  word-wrap: break-word;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: tooltipFadeIn 0.15s ease-out;
  max-width: 280px;
  opacity: 0;
  animation-fill-mode: forwards;
  will-change: transform, opacity;

  ${(props) => {
    switch (props.position) {
      case 'top':
        return `
          transform: translateX(-50%) translateY(-100%);
        `;
      case 'bottom':
        return `
          transform: translateX(-50%);
        `;
      case 'left':
        return `
          transform: translateX(-100%) translateY(-50%);
        `;
      case 'right':
        return `
          transform: translateY(-50%);
        `;
      default:
        return '';
    }
  }}

  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: ${(props) => {
        switch (props.position) {
          case 'top':
            return 'translateX(-50%) translateY(4px)';
          case 'bottom':
            return 'translateX(-50%) translateY(-4px)';
          case 'left':
            return 'translateY(-50%) translateX(4px)';
          case 'right':
            return 'translateY(-50%) translateX(-4px)';
          default:
            return 'translateX(-50%) translateY(4px)';
        }
      }};
    }
    to {
      opacity: 1;
      transform: ${(props) => {
        switch (props.position) {
          case 'top':
          case 'bottom':
            return 'translateX(-50%) translateY(0)';
          case 'left':
          case 'right':
            return 'translateY(-50%) translateX(0)';
          default:
            return 'translateX(-50%) translateY(0)';
        }
      }};
    }
  }
`;

const TooltipArrow = styled.div<{
  position: 'top' | 'bottom' | 'left' | 'right';
  $theme: any;
}>`
  position: absolute;
  width: 0;
  height: 0;
  border: 4px solid transparent;

  ${(props) => {
    switch (props.position) {
      case 'top':
        return `
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-top-color: ${props.$theme.colors.messageBoxBg};
        `;
      case 'bottom':
        return `
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          border-bottom-color: ${props.$theme.colors.messageBoxBg};
        `;
      case 'left':
        return `
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          border-left-color: ${props.$theme.colors.messageBoxBg};
        `;
      case 'right':
        return `
          right: 100%;
          top: 50%;
          transform: translateY(-50%);
          border-right-color: ${props.$theme.colors.messageBoxBg};
        `;
      default:
        return '';
    }
  }}
`;

export default Tooltip;
